import { useEffect, useState } from 'react';
import Uppy from '@uppy/core';
import { Dashboard } from '@uppy/react';
import ImageEditor from '@uppy/image-editor';
import Webcam from '@uppy/webcam';
import XHRUpload from '@uppy/xhr-upload';

// Cloud service providers
import GoogleDrive from '@uppy/google-drive';
import Facebook from '@uppy/facebook';
import Instagram from '@uppy/instagram';
import OneDrive from '@uppy/onedrive';
import Dropbox from '@uppy/dropbox';

import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css';
import '@uppy/webcam/css/style.min.css';
import { formatFileSize } from '../../util/formatFileSize';

const CLOUD_NAME = process.env.REACT_APP_CLOUD_NAME;
const UPLOAD_PRESET = 'unsigned_uploads';
const COMPANION_URL = process.env.REACT_APP_COMPANION_URL;

export default function FileUploader({
  onChange = () => {},
  single = false,
  accept = 'image/*',
  onRemove = () => {},
}) {
  const [, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadComplete, setUploadComplete] = useState(false);

  const [uppy] = useState(() => {
    const uppyInstance = new Uppy({
      id: 'file-uploader',
      restrictions: {
        maxNumberOfFiles: single ? 1 : 20,
        maxFileSize: 10 * 1024 * 1024, // 10MB
        // ✅ FIX: Accept more image formats + validate by MIME type
        allowedFileTypes:
          accept === 'image/*'
            ? ['image/*', '.jpg', '.jpeg', '.png', '.webp', '.bmp', '.gif', '.heic', '.heif']
            : ['image/*', '.pdf', '.jpg', '.jpeg', '.png'],
      },
      autoProceed: false,
    });

    // 🧩 Image Editor plugin
    uppyInstance.use(ImageEditor, {
      id: 'ImageEditor',
      cropperOptions: {
        aspectRatio: undefined,
        viewMode: 1,
      },
      actions: {
        revert: true,
        rotate: true,
        granularRotate: true,
        flip: true,
        zoomIn: true,
        zoomOut: true,
        cropSquare: true,
        cropWidescreen: true,
        cropWidescreenVertical: true,
      },
    });

    // 📸 Webcam plugin
    uppyInstance.use(Webcam, { id: 'Webcam' });

    // ☁️ Cloud Providers (via Companion)
    uppyInstance.use(GoogleDrive, { id: 'GoogleDrive', companionUrl: COMPANION_URL });
    uppyInstance.use(Dropbox, { id: 'Dropbox', companionUrl: COMPANION_URL });
    uppyInstance.use(OneDrive, { id: 'OneDrive', companionUrl: COMPANION_URL });
    uppyInstance.use(Instagram, { id: 'Instagram', companionUrl: COMPANION_URL });
    uppyInstance.use(Facebook, { id: 'Facebook', companionUrl: COMPANION_URL });

    // 🚀 Cloudinary Upload Integration
    uppyInstance.use(XHRUpload, {
      id: 'XHRUpload',
      endpoint: `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
      formData: true,
      fieldName: 'file',
      bundle: false,

      // 🔧 Only send safe metadata
      allowedMetaFields: ['upload_preset'],

      getResponseData(responseText) {
        try {
          return JSON.parse(responseText?.response || {});
        } catch (err) {
          console.error('Failed to parse Cloudinary response', err);
          return {};
        }
      },
    });

    // Set upload preset before upload
    uppyInstance.setMeta({ upload_preset: UPLOAD_PRESET });

    // ✅ FIX: Handle file-added event to normalize file types from Facebook/Instagram
    uppyInstance.on('file-added', (file) => {
      console.log('📁 File added:', file.name, file.type, file.isRemote);
      
      // If file type is missing or invalid, try to infer it
      if (file.isRemote && (!file.type || file.type === 'application/octet-stream')) {
        const extension = file.name.split('.').pop().toLowerCase();
        const mimeTypes = {
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'gif': 'image/gif',
          'webp': 'image/webp',
          'bmp': 'image/bmp',
          'heic': 'image/heic',
          'heif': 'image/heif',
        };
        
        if (mimeTypes[extension]) {
          uppyInstance.setFileMeta(file.id, { type: mimeTypes[extension] });
          console.log(`✅ Fixed file type for ${file.name}: ${mimeTypes[extension]}`);
        }
      }
    });

    uppyInstance.on('upload', () => {
      setIsUploading(true);
    });

    return uppyInstance;
  });

  // 🟢 Handle Upload Events (AFTER upload completes)
  useEffect(() => {
    const handleSuccess = async (file, response) => {
      try {
        console.log('📦 File uploaded:', file);
        console.log('📤 Cloudinary Response:', response);

        const res = response?.body || {};
        const imageUrl = res.secure_url || res.url || file.preview;

        // 🧠 If the file is remote and file.data is not a File object
        if (file.isRemote && !(file.data instanceof File)) {
          try {
            console.log('🌐 Converting remote file to JS File object inside handleSuccess...');
            const downloadRes = await fetch(imageUrl);

            if (!downloadRes.ok) {
              throw new Error(`Failed to fetch remote file: ${downloadRes.status}`);
            }

            const blob = await downloadRes.blob();
            const jsFile = new File([blob], file.name, { type: file.type || blob.type });

            file.data = jsFile;
          } catch (downloadErr) {
            console.error('❌ Error converting remote file:', downloadErr);
          }
        }

        const fileData = {
          file,
          url: imageUrl,
          publicId: res.public_id,
          filename: res.original_filename,
          size: formatFileSize(file.size),
          filetype:
            res.format?.toUpperCase() ||
            file.type?.split('/')[1]?.toUpperCase(),
          caption: '',
        };

        if (single) {
          setUploadedFiles([fileData]);
        } else {
          setUploadedFiles((prev) => [...prev, fileData]);
        }

        console.log('✅ Uploaded to Cloudinary:', fileData);
      } catch (err) {
        console.error('❌ Error handling upload success:', err);
      }
    };

    const handleError = (file, error) => {
      console.error('❌ Upload error:', file?.name, error);
      setIsUploading(false);
    };

    const handleComplete = async (result) => {
      console.log('result', result);
      setIsUploading(false);
      
      // ✅ Construct fileData array from result.successful
      if (result.successful.length > 0) {
        const fileDataArray = await Promise.all(
          result.successful.map(async (item) => {
            const file = item;
            const res = item.response?.body || {};
            const imageUrl = res.secure_url || res.url || file.preview;

            // 🧠 Handle remote file conversion if needed
            if (file.isRemote && !(file.data instanceof File)) {
              try {
                console.log('🌐 Converting remote file to JS File object...');
                const downloadRes = await fetch(imageUrl);
                if (downloadRes.ok) {
                  const blob = await downloadRes.blob();
                  file.data = new File([blob], file.name, { type: file.type || blob.type });
                }
              } catch (downloadErr) {
                console.error('❌ Error converting remote file:', downloadErr);
              }
            }

            return {
              file,
              url: imageUrl,
              publicId: res.public_id,
              filename: res.original_filename,
              size: formatFileSize(file.size),
              filetype:
                res.format?.toUpperCase() ||
                file.type?.split('/')[1]?.toUpperCase(),
              caption: '',
            };
          })
        );

        console.log('🎉 Sending all files to parent:', fileDataArray);
        onChange(fileDataArray);
      }
    };

    uppy.on('upload-success', handleSuccess);
    uppy.on('upload-error', handleError);
    uppy.on('complete', handleComplete);

    return () => {
      uppy.off('upload-success', handleSuccess);
      uppy.off('upload-error', handleError);
      uppy.off('complete', handleComplete);
    };
  }, [uppy, single]);

  // ✅ Send all files to parent when upload is complete
  /* useEffect(() => {
    if (uploadComplete && uploadedFiles.length > 0) {
      console.log('🎉 Sending all files to parent:', uploadedFiles);
      onChange(uploadedFiles);
      setUploadComplete(false); // Reset for next batch
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadComplete]); */

  // 🧹 Cleanup
  useEffect(() => {
    return () => {
      // Clean up object URLs to prevent memory leaks
      uppy.getFiles().forEach(file => {
        if (file.preview && file.preview.startsWith('blob:')) {
          URL.revokeObjectURL(file.preview);
        }
      });
      // uppy.close();
      // uppy.destroy(); // Uncomment if you want full cleanup
    };
  }, [uppy]);

  return (
    <Dashboard
      uppy={uppy}
      height={480}
      width="100%"
      showProgressDetails={true}
      note={`Drag and drop or click to browse • Supports: ${
        accept === 'image/*'
          ? 'JPG, JPEG, PNG, WEBP, BMP, GIF, HEIC'
          : 'JPG, JPEG, PNG, PDF'
      } • Max file size: 10MB`}
      proudlyDisplayPoweredByUppy={false}
      showLinkToFileUploadResult={false}
      showRemoveButtonAfterComplete={false}
      theme="light"
      plugins={[
        'ImageEditor',
        'Webcam',
        'GoogleDrive',
        'Dropbox',
        'OneDrive',
        'Instagram',
        'Facebook',
      ]}
    />
  );
}